import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Upload, FilePlus2, Clock, CheckCircle, AlertCircle, FolderDown, Calendar, FileText, X, FilePenLine, Trash2 } from "lucide-react";

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

function getCurrentQuarter(date = new Date()) {
  const m = date.getMonth(); // 0..11
  return Math.floor(m / 3) + 1; // 1..4
}

function formatDateTime(dt) {
  try {
    const d = typeof dt === 'string' ? new Date(dt) : dt;
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    }).format(d);
  } catch {
    return dt || '';
  }
}

export default function OPDPlansReports() {
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [activeTab, setActiveTab] = useState("submit"); // 'submit' | 'history'

  const [yearlyFile, setYearlyFile] = useState(null);
  const [quarterlyFile, setQuarterlyFile] = useState(null);
  const [annualFile, setAnnualFile] = useState(null);

  const [isDraggingYearly, setIsDraggingYearly] = useState(false);
  const [isDraggingQuarterly, setIsDraggingQuarterly] = useState(false);
  const [isDraggingAnnual, setIsDraggingAnnual] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const currentUserEmail = currentUser?.email || "";
  const [opdSubCity, setOpdSubCity] = useState("");
  const currentYear = new Date().getFullYear();
  const currentQuarter = getCurrentQuarter();

  // Edit/Delete state
  const [editingItem, setEditingItem] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // History filters
  const [filterYear, setFilterYear] = useState("");
  const [filterType, setFilterType] = useState("");
  const availableYears = useMemo(() => {
    const ys = new Set();
    submissions.forEach((s) => {
      const y = Number(s.year || s.planYear);
      if (Number.isFinite(y)) ys.add(y);
    });
    return Array.from(ys).sort((a,b) => b - a);
  }, [submissions]);

  const filteredHistory = useMemo(() => {
    return submissions.filter((s) => {
      const y = Number(s.year || s.planYear);
      const t = (s.type || s.uploadType || '').toString().toLowerCase();
      if (filterYear && (!Number.isFinite(y) || y !== Number(filterYear))) return false;
      if (filterType && t !== filterType.toLowerCase()) return false;
      return true;
    });
  }, [submissions, filterYear, filterType]);

  // Helper to show transient notices
  const showNotice = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 3000);
  };

  // Edit existing submission (replace file)
  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingItem) return;
    if (!editFile) { showNotice('Please choose a replacement file'); return; }
    try {
      setActionLoading(true);
      const fd = new FormData();
      fd.append('id', String(editingItem.id));
      fd.append('file', editFile);
      fd.append('requestEmail', currentUserEmail);
      const res = await fetch(`${API_BASE_URL}/update_plan_report.php`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || `Update failed (HTTP ${res.status})`);
      }
      showNotice('Updated successfully');
      setEditingItem(null);
      setEditFile(null);
      await fetchSubmissions();
    } catch (e) {
      console.error('update error', e);
      setError(e.message || 'Update failed');
    } finally {
      setActionLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditFile(null);
  };

  // Delete submission
  const deleteSubmission = async (item) => {
    if (!item) return;
    if (!window.confirm('Delete this submission? This cannot be undone.')) return;
    try {
      setActionLoading(true);
      const res = await fetch(`${API_BASE_URL}/delete_plan_report.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, requestEmail: currentUserEmail })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || `Delete failed (HTTP ${res.status})`);
      }
      showNotice('Deleted successfully');
      await fetchSubmissions();
    } catch (e) {
      console.error('delete error', e);
      setError(e.message || 'Delete failed');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchSubmissions = useCallback(async () => {
    if (!currentUserEmail) return;
    setLoading(true);
    setError("");
    try {
      // Resolve OPD sub city using current user's email (same as MembersPage)
      let subCity = opdSubCity;
      if (!subCity) {
        const profRes = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(currentUserEmail)}`);
        const prof = await profRes.json();
        if (prof?.status === 'success' && prof?.data?.sub_city) {
          subCity = prof.data.sub_city;
          setOpdSubCity(subCity);
        } else {
          setSubmissions([]);
          setLoading(false);
          return;
        }
      }

      const res = await fetch(`${API_BASE_URL}/get_plan_reports.php?email=${encodeURIComponent(currentUserEmail)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      const norm = (s) => (s || '').toString().trim().toLowerCase();
      const subCityNorm = norm(subCity);
      const bySubCity = list.filter((r) => norm(r.sub_city || r.subCity) === subCityNorm);
      setSubmissions(bySubCity);
    } catch (e) {
      console.error('fetchSubmissions error', e);
      setError('Failed to load submissions');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [currentUserEmail, opdSubCity]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // No per-period constraints: users can submit multiple yearly plans and quarterly reports

  // Drag & drop handlers (shared)
  const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };

  const onDropYearly = (e) => {
    prevent(e);
    setIsDraggingYearly(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setYearlyFile(e.dataTransfer.files[0]);
    }
  };

  const onDropQuarterly = (e) => {
    prevent(e);
    setIsDraggingQuarterly(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setQuarterlyFile(e.dataTransfer.files[0]);
    }
  };

  const onDropAnnual = (e) => {
    prevent(e);
    setIsDraggingAnnual(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setAnnualFile(e.dataTransfer.files[0]);
    }
  };

  // Upload actions
  const uploadFile = async ({ file, type, year, quarter }) => {
    if (!file) {
      showNotice('Please choose a file first');
      return false;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', type); // 'yearly' | 'quarterly' | 'annual'
      fd.append('year', String(year));
      if (quarter) fd.append('quarter', String(quarter));
      fd.append('requestEmail', currentUserEmail);

      const res = await fetch(`${API_BASE_URL}/upload_plan_report.php`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || `Upload failed (HTTP ${res.status})`);
      }
      showNotice('Uploaded successfully');
      setYearlyFile(null);
      setQuarterlyFile(null);
      setAnnualFile(null);
      await fetchSubmissions();
      return true;
    } catch (e) {
      console.error('upload error', e);
      setError(e.message || 'Upload failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const submitYearly = async (e) => {
    e.preventDefault();
    await uploadFile({ file: yearlyFile, type: 'yearly', year: currentYear });
  };

  const submitQuarterly = async (e) => {
    e.preventDefault();
    await uploadFile({ file: quarterlyFile, type: 'quarterly', year: currentYear, quarter: currentQuarter });
  };

  const submitAnnual = async (e) => {
    e.preventDefault();
    await uploadFile({ file: annualFile, type: 'annual', year: currentYear });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-5 py-6 text-white">
            <div className="flex items-center space-x-3">
              <FolderDown className="w-7 h-7" />
              <div>
                <h1 className="text-2xl font-bold">Plans & Reports</h1>
                <p className="text-teal-100 text-sm">Submit your yearly plan, quarterly reports, and annual report. Only you can see your submissions.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Notices */}
        {(notice || error) && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'} border ${error ? 'border-red-200' : 'border-green-200'}`}>
            {error ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            <span className="text-sm">{error || notice}</span>
            <button className="ml-auto text-gray-400 hover:text-gray-600" onClick={() => { setError(''); setNotice(''); }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-3 mb-4">
          <button
            className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'submit' ? 'bg-teal-600 text-white shadow' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            onClick={() => setActiveTab('submit')}
            type="button"
          >
            Submissions
          </button>
          <button
            className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'history' ? 'bg-teal-600 text-white shadow' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            onClick={() => setActiveTab('history')}
            type="button"
          >
            History ({submissions.length})
          </button>
        </div>

        {/* Upload forms (Submit tab) */}
        {activeTab === 'submit' && (
        <div className="grid md:grid-cols-3 gap-4">
          {/* Yearly Plan */}
          <form onSubmit={submitYearly} className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-gray-800">Yearly Plan</h2>
            </div>
            <p className="text-sm text-gray-500">Current year: {currentYear}. You can submit Annual Plan.</p>

            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center transition ${isDraggingYearly ? 'border-teal-500 bg-teal-50' : 'border-gray-300 bg-gray-50'}`}
              onDragEnter={(e) => { prevent(e); setIsDraggingYearly(true); }}
              onDragOver={prevent}
              onDragLeave={(e) => { prevent(e); setIsDraggingYearly(false); }}
              onDrop={onDropYearly}
            >
              <Upload className="w-6 h-6 text-teal-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Drag & drop your yearly plan here, or click below</p>
              <div className="mt-3">
                <label className="inline-flex items-center px-3 py-2 bg-teal-600 text-white rounded-lg shadow hover:bg-teal-700 cursor-pointer">
                  <FilePlus2 className="w-4 h-4 mr-2" />
                  <span>Choose File</span>
                  <input type="file" className="hidden" onChange={(e) => setYearlyFile(e.target.files?.[0] || null)} />
                </label>
              </div>
              {yearlyFile && (
                <p className="mt-2 text-sm text-gray-700">Selected: <span className="font-medium">{yearlyFile.name}</span></p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !yearlyFile}
              className="w-full inline-flex justify-center items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg disabled:opacity-50 hover:bg-teal-700"
            >
              <FolderDown className="w-4 h-4" /> Submit Yearly Plan
            </button>
          </form>

          {/* Quarterly Report */}
          <form onSubmit={submitQuarterly} className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-800">Quarterly Report</h2>
            </div>
            <p className="text-sm text-gray-500">Current period: {currentYear}, Q{currentQuarter}. You can submit multiple reports this quarter.</p>

            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center transition ${isDraggingQuarterly ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}
              onDragEnter={(e) => { prevent(e); setIsDraggingQuarterly(true); }}
              onDragOver={prevent}
              onDragLeave={(e) => { prevent(e); setIsDraggingQuarterly(false); }}
              onDrop={onDropQuarterly}
            >
              <Upload className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Drag & drop your quarterly report here, or click below</p>
              <div className="mt-3">
                <label className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 cursor-pointer">
                  <FilePlus2 className="w-4 h-4 mr-2" />
                  <span>Choose File</span>
                  <input type="file" className="hidden" onChange={(e) => setQuarterlyFile(e.target.files?.[0] || null)} />
                </label>
              </div>
              {quarterlyFile && (
                <p className="mt-2 text-sm text-gray-700">Selected: <span className="font-medium">{quarterlyFile.name}</span></p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !quarterlyFile}
              className="w-full inline-flex justify-center items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700"
            >
              <FolderDown className="w-4 h-4" /> Submit Quarterly Report
            </button>
          </form>

          {/* Annual Report */}
          <form onSubmit={submitAnnual} className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-800">Annual Report</h2>
            </div>
            <p className="text-sm text-gray-500">Current year: {currentYear}. You can submit your annual report.</p>

            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center transition ${isDraggingAnnual ? 'border-purple-500 bg-purple-50' : 'border-gray-300 bg-gray-50'}`}
              onDragEnter={(e) => { prevent(e); setIsDraggingAnnual(true); }}
              onDragOver={prevent}
              onDragLeave={(e) => { prevent(e); setIsDraggingAnnual(false); }}
              onDrop={onDropAnnual}
            >
              <Upload className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Drag & drop your annual report here, or click below</p>
              <div className="mt-3">
                <label className="inline-flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 cursor-pointer">
                  <FilePlus2 className="w-4 h-4 mr-2" />
                  <span>Choose File</span>
                  <input type="file" className="hidden" onChange={(e) => setAnnualFile(e.target.files?.[0] || null)} />
                </label>
              </div>
              {annualFile && (
                <p className="mt-2 text-sm text-gray-700">Selected: <span className="font-medium">{annualFile.name}</span></p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !annualFile}
              className="w-full inline-flex justify-center items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg disabled:opacity-50 hover:bg-purple-700"
            >
              <FolderDown className="w-4 h-4" /> Submit Annual Report
            </button>
          </form>
        </div>
        )}

        {/* Submission history (History tab) */}
        {activeTab === 'history' && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-gray-600" />
            <h3 className="text-base font-semibold text-gray-800">Submission History</h3>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">All Years</option>
                {availableYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">All Types</option>
                <option value="yearly">Plan (Yearly)</option>
                <option value="quarterly">Quarterly Report</option>
                <option value="annual">Annual Report</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => { setFilterYear(''); setFilterType(''); }}
                className="px-3 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800"
              >
                Reset
              </button>
              <div className="text-sm text-gray-600 ml-auto self-center">
                Showing {filteredHistory.length} of {submissions.length}
              </div>
            </div>
          </div>

          {loading && (
            <p className="text-sm text-gray-500">Loading...</p>
          )}

          {!loading && filteredHistory.length === 0 && (
            <p className="text-sm text-gray-500">No submissions yet.</p>
          )}

          {!loading && filteredHistory.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 bg-gray-50">
                    <th className="px-3 py-2">File</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Year</th>
                    <th className="px-3 py-2">Quarter</th>
                    <th className="px-3 py-2">Submitted At</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {filteredHistory
                    .slice()
                    .sort((a,b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0))
                    .map((s, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <span className="text-gray-800 font-medium">{s.filename || s.fileName || 'file'}</span>
                        </td>
                        <td className="px-3 py-2">
                          {(() => {
                            const tRaw = (s.type || s.uploadType || '').toString().toLowerCase();
                            const badgeClass = tRaw === 'yearly'
                              ? 'bg-teal-100 text-teal-700'
                              : tRaw === 'quarterly'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-purple-100 text-purple-700';
                            const label = tRaw === 'yearly'
                              ? 'YEARLY PLAN'
                              : tRaw === 'quarterly'
                                ? 'QUARTERLY REPORT'
                                : tRaw === 'annual'
                                  ? 'ANNUAL REPORT'
                                  : (s.type || s.uploadType || '').toString().toUpperCase() || '-';
                            return (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                                {label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2">{s.year || s.planYear || '-'}</td>
                        <td className="px-3 py-2">{s.quarter || s.planQuarter || '-'}</td>
                        <td className="px-3 py-2 text-gray-600">{formatDateTime(s.created_at || s.createdAt)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <button
                              className="inline-flex items-center gap-1 px-2 py-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                              onClick={() => setEditingItem(s)}
                              disabled={actionLoading}
                              title="Edit / Replace file"
                            >
                              <FilePenLine className="w-4 h-4" />
                              <span className="hidden sm:inline">Edit</span>
                            </button>
                            <button
                              className="inline-flex items-center gap-1 px-2 py-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                              onClick={() => deleteSubmission(s)}
                              disabled={actionLoading}
                              title="Delete submission"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="hidden sm:inline">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Inline edit panel */}
          {editingItem && (
            <form onSubmit={submitEdit} className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <FilePenLine className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-gray-800">Replace file for: <span className="text-gray-700">{editingItem.filename || editingItem.fileName}</span></h4>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 cursor-pointer">
                  <FilePlus2 className="w-4 h-4 mr-2" />
                  <span>Choose New File</span>
                  <input type="file" className="hidden" onChange={(e) => setEditFile(e.target.files?.[0] || null)} />
                </label>
                {editFile && (
                  <span className="text-sm text-gray-700">Selected: <span className="font-medium">{editFile.name}</span></span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <button type="button" onClick={cancelEdit} className="px-3 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800">Cancel</button>
                  <button type="submit" disabled={actionLoading || !editFile} className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">Save</button>
                </div>
              </div>
            </form>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
